import React from 'react';
import { motion } from 'framer-motion';

// ============================================================
// PANTASS PET SPRITES v2 — Tamagotchi-quality SVG illustrations
// Each pet has 4 dramatically different stages + 6 moods
// Moods: happy, sad, eating, sleeping, excited, hungry
// ============================================================

// === HARIMAU MALAYA (TIGER) ===
function HarimauSprite({ stage = 1, size = 120, mood = 'happy' }) {
  // Stage scaling — baby is tiny blob, adult is majestic
  const headY = [52, 44, 38, 32][stage - 1];
  const headR = [22, 24, 22, 20][stage - 1];
  const bodyW = [20, 26, 30, 34][stage - 1];
  const bodyH = [16, 20, 26, 30][stage - 1];
  const bodyY = [72, 68, 64, 60][stage - 1];

  const eyeSize = stage === 1 ? 5.5 : stage === 2 ? 5 : 4.5;
  const pupilSize = stage === 1 ? 3.5 : 3;
  const eyeY = headY - 2;
  const eyeSpread = stage === 1 ? 10 : 11;

  // Mood-based eye rendering
  const renderEyes = () => {
    const lx = 60 - eyeSpread;
    const rx = 60 + eyeSpread;

    if (mood === 'sleeping') {
      return (<>
        <path d={`M${lx - 4} ${eyeY} Q${lx} ${eyeY + 3} ${lx + 4} ${eyeY}`} stroke="#92400E" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d={`M${rx - 4} ${eyeY} Q${rx} ${eyeY + 3} ${rx + 4} ${eyeY}`} stroke="#92400E" strokeWidth="2" fill="none" strokeLinecap="round" />
        <text x="80" y={headY - 12} fill="#94a3b8" fontSize="10" fontWeight="bold" opacity="0.6">z z</text>
      </>);
    }
    if (mood === 'excited') {
      return (<>
        <polygon points={`${lx},${eyeY - 4} ${lx + 3},${eyeY + 3} ${lx - 3},${eyeY + 3}`} fill="#FBBF24" />
        <polygon points={`${lx - 3},${eyeY - 1} ${lx + 3},${eyeY - 1} ${lx},${eyeY + 4}`} fill="#FBBF24" />
        <polygon points={`${rx},${eyeY - 4} ${rx + 3},${eyeY + 3} ${rx - 3},${eyeY + 3}`} fill="#FBBF24" />
        <polygon points={`${rx - 3},${eyeY - 1} ${rx + 3},${eyeY - 1} ${rx},${eyeY + 4}`} fill="#FBBF24" />
      </>);
    }
    if (mood === 'eating') {
      return (<>
        <path d={`M${lx - 3} ${eyeY} Q${lx} ${eyeY - 3} ${lx + 3} ${eyeY}`} stroke="#1E293B" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d={`M${rx - 3} ${eyeY} Q${rx} ${eyeY - 3} ${rx + 3} ${eyeY}`} stroke="#1E293B" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      </>);
    }

    const isSad = mood === 'sad' || mood === 'hungry';
    return (<>
      <ellipse cx={lx} cy={eyeY} rx={eyeSize} ry={isSad ? eyeSize * 0.85 : eyeSize} fill="white" />
      <ellipse cx={rx} cy={eyeY} rx={eyeSize} ry={isSad ? eyeSize * 0.85 : eyeSize} fill="white" />
      <circle cx={lx + 1} cy={eyeY + (isSad ? 1 : -0.5)} r={pupilSize} fill="#1E293B" />
      <circle cx={rx + 1} cy={eyeY + (isSad ? 1 : -0.5)} r={pupilSize} fill="#1E293B" />
      <circle cx={lx + 2} cy={eyeY - 1.5} r={1.3} fill="white" />
      <circle cx={rx + 2} cy={eyeY - 1.5} r={1.3} fill="white" />
      {mood === 'hungry' && <circle cx={lx - 2} cy={eyeY + 3} r={1} fill="#60A5FA" opacity="0.7" />}
    </>);
  };

  // Mood-based mouth
  const renderMouth = () => {
    const mouthY = headY + headR * 0.45;
    if (mood === 'eating') {
      return <ellipse cx="60" cy={mouthY} rx="5" ry="4" fill="#92400E" />;
    }
    if (mood === 'excited') {
      return <ellipse cx="60" cy={mouthY + 1} rx="6" ry="4.5" fill="#92400E" />;
    }
    if (mood === 'sad' || mood === 'hungry') {
      return <path d={`M54 ${mouthY + 2} Q60 ${mouthY - 2} 66 ${mouthY + 2}`} stroke="#92400E" strokeWidth="2" fill="none" strokeLinecap="round" />;
    }
    if (mood === 'sleeping') {
      return <path d={`M56 ${mouthY} Q60 ${mouthY + 2} 64 ${mouthY}`} stroke="#92400E" strokeWidth="1.5" fill="none" strokeLinecap="round" />;
    }
    // happy
    return <path d={`M53 ${mouthY} Q60 ${mouthY + 6} 67 ${mouthY}`} stroke="#92400E" strokeWidth="2" fill="none" strokeLinecap="round" />;
  };

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
      {/* Shadow */}
      <ellipse cx="60" cy="105" rx={bodyW * 0.8} ry="4" fill="#0F172A" opacity="0.3" />

      {/* Tail — grows with stage */}
      {stage >= 2 && (
        <path d={`M${60 + bodyW - 2} ${bodyY} Q${70 + stage * 5} ${bodyY - 15 - stage * 3} ${65 + stage * 4} ${bodyY - 25 - stage * 2}`}
          stroke="#F97316" strokeWidth={3 + stage} strokeLinecap="round" fill="none" />
      )}

      {/* Body */}
      <ellipse cx="60" cy={bodyY} rx={bodyW} ry={bodyH} fill="#F97316" />
      {/* Belly */}
      <ellipse cx="60" cy={bodyY + bodyH * 0.2} rx={bodyW * 0.55} ry={bodyH * 0.6} fill="#FED7AA" />

      {/* Stripes — more with each stage */}
      {stage >= 2 && <>
        <path d={`M${52} ${bodyY - bodyH * 0.3} L${48} ${bodyY + bodyH * 0.2}`} stroke="#92400E" strokeWidth={stage > 2 ? 2.5 : 2} strokeLinecap="round" />
        <path d={`M${68} ${bodyY - bodyH * 0.3} L${72} ${bodyY + bodyH * 0.2}`} stroke="#92400E" strokeWidth={stage > 2 ? 2.5 : 2} strokeLinecap="round" />
      </>}
      {stage >= 3 && <>
        <path d={`M${56} ${bodyY - bodyH * 0.4} L${54} ${bodyY}`} stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
        <path d={`M${64} ${bodyY - bodyH * 0.4} L${66} ${bodyY}`} stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
      </>}
      {stage >= 4 && <>
        <path d={`M${44} ${bodyY} L${40} ${bodyY + bodyH * 0.3}`} stroke="#92400E" strokeWidth="2.5" strokeLinecap="round" />
        <path d={`M${76} ${bodyY} L${80} ${bodyY + bodyH * 0.3}`} stroke="#92400E" strokeWidth="2.5" strokeLinecap="round" />
      </>}

      {/* Paws */}
      <ellipse cx={60 - bodyW * 0.45} cy={bodyY + bodyH - 2} rx={6 + stage} ry={4 + stage * 0.5} fill="#F97316" />
      <ellipse cx={60 + bodyW * 0.45} cy={bodyY + bodyH - 2} rx={6 + stage} ry={4 + stage * 0.5} fill="#F97316" />
      {/* Paw pads */}
      {stage >= 3 && <>
        <circle cx={60 - bodyW * 0.45} cy={bodyY + bodyH} r={2} fill="#FED7AA" />
        <circle cx={60 + bodyW * 0.45} cy={bodyY + bodyH} r={2} fill="#FED7AA" />
      </>}

      {/* Head */}
      <circle cx="60" cy={headY} r={headR} fill="#F97316" />

      {/* Head stripes */}
      {stage >= 3 && <>
        <path d={`M60 ${headY - headR + 2} L58 ${headY - headR * 0.3}`} stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
        <path d={`M60 ${headY - headR + 2} L62 ${headY - headR * 0.3}`} stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
      </>}

      {/* Ears */}
      <circle cx={60 - headR + 4} cy={headY - headR + 5} r={6 + stage * 0.5} fill="#F97316" />
      <circle cx={60 + headR - 4} cy={headY - headR + 5} r={6 + stage * 0.5} fill="#F97316" />
      <circle cx={60 - headR + 4} cy={headY - headR + 5} r={3.5 + stage * 0.3} fill="#FBBF24" />
      <circle cx={60 + headR - 4} cy={headY - headR + 5} r={3.5 + stage * 0.3} fill="#FBBF24" />

      {/* Eyes */}
      {renderEyes()}

      {/* Nose */}
      <ellipse cx="60" cy={headY + headR * 0.25} rx="3.5" ry="2.5" fill="#92400E" />
      {/* Nose highlight */}
      <ellipse cx="59" cy={headY + headR * 0.22} rx="1.5" ry="1" fill="#B45309" opacity="0.5" />

      {/* Mouth */}
      {renderMouth()}

      {/* Whiskers */}
      {stage >= 2 && <>
        <line x1={60 - headR - 4} y1={headY + 3} x2={60 - eyeSpread + 2} y2={headY + 5} stroke="#D97706" strokeWidth="1" opacity="0.4" />
        <line x1={60 - headR - 3} y1={headY + 7} x2={60 - eyeSpread + 2} y2={headY + 6} stroke="#D97706" strokeWidth="1" opacity="0.4" />
        <line x1={60 + headR + 4} y1={headY + 3} x2={60 + eyeSpread - 2} y2={headY + 5} stroke="#D97706" strokeWidth="1" opacity="0.4" />
        <line x1={60 + headR + 3} y1={headY + 7} x2={60 + eyeSpread - 2} y2={headY + 6} stroke="#D97706" strokeWidth="1" opacity="0.4" />
      </>}

      {/* Cheeks */}
      <circle cx={60 - eyeSpread - 3} cy={headY + 5} r={3.5} fill="#FBBF24" opacity="0.25" />
      <circle cx={60 + eyeSpread + 3} cy={headY + 5} r={3.5} fill="#FBBF24" opacity="0.25" />

      {/* Adult crown */}
      {stage === 4 && (
        <g>
          <path d={`M46 ${headY - headR - 2} L50 ${headY - headR - 14} L54 ${headY - headR - 6} L60 ${headY - headR - 16} L66 ${headY - headR - 6} L70 ${headY - headR - 14} L74 ${headY - headR - 2}`}
            fill="#FBBF24" stroke="#F59E0B" strokeWidth="1.5" />
          <circle cx="60" cy={headY - headR - 12} r="2" fill="#F43F5E" />
        </g>
      )}

      {/* Eating food particle */}
      {mood === 'eating' && <>
        <circle cx="72" cy={headY + headR * 0.3} r="3" fill="#34D399" />
        <circle cx="76" cy={headY + headR * 0.1} r="2" fill="#34D399" opacity="0.7" />
      </>}
    </svg>
  );
}

// === BURUNG ENGGANG (HORNBILL) ===
function EnggangSprite({ stage = 1, size = 120, mood = 'happy' }) {
  const headY = [46, 40, 36, 30][stage - 1];
  const bodyY = [70, 66, 62, 58][stage - 1];
  const bodyW = [16, 20, 24, 28][stage - 1];
  const bodyH = [18, 22, 26, 30][stage - 1];

  const eyeY = headY - 1;

  const renderEyes = () => {
    if (mood === 'sleeping') {
      return (<>
        <path d={`M62 ${eyeY} Q65 ${eyeY + 2} 68 ${eyeY}`} stroke="#1E293B" strokeWidth="2" fill="none" strokeLinecap="round" />
        <text x="78" y={headY - 10} fill="#94a3b8" fontSize="9" fontWeight="bold" opacity="0.5">z z</text>
      </>);
    }
    if (mood === 'excited') {
      return (<>
        <polygon points={`65,${eyeY - 4} 68,${eyeY + 2} 62,${eyeY + 2}`} fill="#FBBF24" />
        <polygon points={`62,${eyeY - 1} 68,${eyeY - 1} 65,${eyeY + 4}`} fill="#FBBF24" />
      </>);
    }
    if (mood === 'eating') {
      return <path d={`M62 ${eyeY} Q65 ${eyeY - 2} 68 ${eyeY}`} stroke="#1E293B" strokeWidth="2.5" fill="none" strokeLinecap="round" />;
    }
    const isSad = mood === 'sad' || mood === 'hungry';
    return (<>
      <circle cx={65} cy={eyeY} r={4} fill="white" />
      <circle cx={65} cy={eyeY} r={5} stroke="#38BDF8" strokeWidth="1.5" fill="none" />
      <circle cx={65.5} cy={eyeY + (isSad ? 0.5 : -0.5)} r={2.5} fill="#1E293B" />
      <circle cx={66.5} cy={eyeY - 1} r={1} fill="white" />
    </>);
  };

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
      <ellipse cx="60" cy="108" rx={bodyW * 0.6} ry="3" fill="#0F172A" opacity="0.3" />

      {/* Tail feathers */}
      {stage >= 2 && <>
        <rect x="56" y={bodyY + bodyH - 4} width="2" height={10 + stage * 4} rx="1" fill="#F8FAFC" transform={`rotate(-5 57 ${bodyY + bodyH})`} />
        <rect x="60" y={bodyY + bodyH - 4} width="2" height={12 + stage * 4} rx="1" fill="#F8FAFC" />
        <rect x="64" y={bodyY + bodyH - 4} width="2" height={10 + stage * 4} rx="1" fill="#F8FAFC" transform={`rotate(5 65 ${bodyY + bodyH})`} />
      </>}

      {/* Body */}
      <ellipse cx="60" cy={bodyY} rx={bodyW} ry={bodyH} fill="#1E293B" />
      <ellipse cx="60" cy={bodyY + bodyH * 0.15} rx={bodyW * 0.6} ry={bodyH * 0.55} fill="#F8FAFC" />

      {/* Wings */}
      {stage >= 2 && <>
        <ellipse cx={60 - bodyW - 4} cy={bodyY + 2} rx={6 + stage * 2} ry={12 + stage * 2} fill="#334155" transform={`rotate(-10 ${60 - bodyW - 4} ${bodyY + 2})`} />
        <ellipse cx={60 + bodyW + 4} cy={bodyY + 2} rx={6 + stage * 2} ry={12 + stage * 2} fill="#334155" transform={`rotate(10 ${60 + bodyW + 4} ${bodyY + 2})`} />
      </>}

      {/* Head */}
      <circle cx="60" cy={headY} r={12 + stage * 1.5} fill="#1E293B" />

      {/* Casque (hornbill crest) — grows dramatically */}
      <path d={`M${54 - stage} ${headY - 10 - stage * 2} Q60 ${headY - 18 - stage * 4} ${66 + stage} ${headY - 10 - stage * 2}`}
        fill="#FBBF24" stroke="#F59E0B" strokeWidth="1.5" />
      {stage >= 2 && <path d={`M${56} ${headY - 12 - stage * 2} Q60 ${headY - 18 - stage * 4} ${64} ${headY - 12 - stage * 2}`} fill="#F97316" />}
      {stage >= 4 && <circle cx="60" cy={headY - 18 - stage * 3} r="2.5" fill="#F43F5E" />}

      {/* Beak — longer with stage */}
      <path d={`M54 ${headY + 1} L${40 - stage * 2} ${headY + 6} Q${38 - stage * 2} ${headY + 8} ${40 - stage * 2} ${headY + 10} L56 ${headY + 5} Z`}
        fill="#FBBF24" />
      <path d={`M54 ${headY + 4} L${42 - stage * 2} ${headY + 8} L56 ${headY + 5} Z`} fill="#F59E0B" />
      {mood === 'eating' && <ellipse cx={38 - stage * 2} cy={headY + 8} rx="3" ry="4" fill="#FBBF24" />}

      {/* Eye */}
      {renderEyes()}

      {/* Feet */}
      <g>
        <line x1="53" y1={bodyY + bodyH - 2} x2="50" y2={bodyY + bodyH + 8} stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="67" y1={bodyY + bodyH - 2} x2="70" y2={bodyY + bodyH + 8} stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="48" y1={bodyY + bodyH + 8} x2="53" y2={bodyY + bodyH + 8} stroke="#F97316" strokeWidth="2" strokeLinecap="round" />
        <line x1="68" y1={bodyY + bodyH + 8} x2="73" y2={bodyY + bodyH + 8} stroke="#F97316" strokeWidth="2" strokeLinecap="round" />
      </g>

      {/* Mood mouth indicator */}
      {(mood === 'happy' || mood === 'excited') && <path d={`M54 ${headY + 6} Q56 ${headY + 9} 58 ${headY + 6}`} stroke="#FBBF24" strokeWidth="1" fill="none" />}
    </svg>
  );
}

// === PENYU AGAR-AGAR (SEA TURTLE) ===
function PenyuSprite({ stage = 1, size = 120, mood = 'happy' }) {
  const shellY = [60, 56, 52, 48][stage - 1];
  const shellW = [22, 28, 32, 36][stage - 1];
  const shellH = [18, 22, 26, 28][stage - 1];
  const headX = [38, 32, 28, 24][stage - 1];
  const headY = [50, 46, 44, 42][stage - 1];
  const headR = [9, 11, 12, 13][stage - 1];

  const eyeX = headX - 2;
  const eyeY = headY - 2;

  const renderEyes = () => {
    if (mood === 'sleeping') {
      return (<>
        <path d={`M${eyeX - 3} ${eyeY} Q${eyeX} ${eyeY + 2} ${eyeX + 3} ${eyeY}`} stroke="#047857" strokeWidth="2" fill="none" strokeLinecap="round" />
        <text x={headX - 16} y={headY - 8} fill="#94a3b8" fontSize="8" fontWeight="bold" opacity="0.5">z z</text>
      </>);
    }
    if (mood === 'excited') {
      return (<>
        <polygon points={`${eyeX},${eyeY - 3} ${eyeX + 2.5},${eyeY + 2} ${eyeX - 2.5},${eyeY + 2}`} fill="#FBBF24" />
        <polygon points={`${eyeX - 2.5},${eyeY} ${eyeX + 2.5},${eyeY} ${eyeX},${eyeY + 3.5}`} fill="#FBBF24" />
      </>);
    }
    if (mood === 'eating') {
      return <path d={`M${eyeX - 2.5} ${eyeY} Q${eyeX} ${eyeY - 2} ${eyeX + 2.5} ${eyeY}`} stroke="#047857" strokeWidth="2" fill="none" strokeLinecap="round" />;
    }
    const isSad = mood === 'sad' || mood === 'hungry';
    return (<>
      <circle cx={eyeX} cy={eyeY} r={3.5} fill="white" />
      <circle cx={eyeX + 0.5} cy={eyeY + (isSad ? 0.5 : -0.5)} r={2} fill="#1E293B" />
      <circle cx={eyeX + 1.5} cy={eyeY - 1} r={0.8} fill="white" />
    </>);
  };

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
      <ellipse cx="60" cy="100" rx={shellW * 0.6} ry="3" fill="#0F172A" opacity="0.25" />

      {/* Flippers — back */}
      <ellipse cx={60 - shellW + 5} cy={shellY + shellH - 4} rx={5 + stage * 1.5} ry={3 + stage * 0.5} fill="#6EE7B7" transform={`rotate(-30 ${60 - shellW + 5} ${shellY + shellH - 4})`} />
      <ellipse cx={60 + shellW - 5} cy={shellY + shellH - 4} rx={5 + stage * 1.5} ry={3 + stage * 0.5} fill="#6EE7B7" transform={`rotate(30 ${60 + shellW - 5} ${shellY + shellH - 4})`} />

      {/* Shell base (darker) */}
      <ellipse cx="60" cy={shellY + 4} rx={shellW} ry={shellH} fill="#059669" />
      {/* Shell top (lighter dome) */}
      <ellipse cx="60" cy={shellY} rx={shellW - 2} ry={shellH - 4} fill="#34D399" />

      {/* Shell pattern — hexagonal at higher stages */}
      {stage >= 1 && <ellipse cx="60" cy={shellY - 2} rx={shellW * 0.35} ry={shellH * 0.3} fill="#059669" opacity="0.4" />}
      {stage >= 2 && <>
        <ellipse cx={60 - shellW * 0.35} cy={shellY + 2} rx={shellW * 0.2} ry={shellH * 0.22} fill="#059669" opacity="0.35" />
        <ellipse cx={60 + shellW * 0.35} cy={shellY + 2} rx={shellW * 0.2} ry={shellH * 0.22} fill="#059669" opacity="0.35" />
      </>}
      {stage >= 3 && <>
        <ellipse cx={60 - shellW * 0.2} cy={shellY + shellH * 0.3} rx={shellW * 0.18} ry={shellH * 0.18} fill="#059669" opacity="0.3" />
        <ellipse cx={60 + shellW * 0.2} cy={shellY + shellH * 0.3} rx={shellW * 0.18} ry={shellH * 0.18} fill="#059669" opacity="0.3" />
      </>}
      {stage >= 4 && <>
        <ellipse cx="60" cy={shellY + shellH * 0.35} rx={shellW * 0.25} ry={shellH * 0.15} fill="#059669" opacity="0.25" />
        <path d={`M${60 - shellW + 8} ${shellY - 2} L${60 + shellW - 8} ${shellY - 2}`} stroke="#059669" strokeWidth="1" opacity="0.3" />
      </>}

      {/* Shell rim highlight */}
      <ellipse cx="60" cy={shellY - shellH * 0.15} rx={shellW * 0.8} ry={2} fill="white" opacity="0.12" />

      {/* Front flippers */}
      <ellipse cx={60 - shellW + 2} cy={shellY - 2} rx={8 + stage * 2} ry={4 + stage * 0.8} fill="#6EE7B7" transform={`rotate(-25 ${60 - shellW + 2} ${shellY - 2})`} />
      <ellipse cx={60 + shellW - 2} cy={shellY - 2} rx={8 + stage * 2} ry={4 + stage * 0.8} fill="#6EE7B7" transform={`rotate(25 ${60 + shellW - 2} ${shellY - 2})`} />

      {/* Tail */}
      {stage >= 2 && <ellipse cx={60 + shellW - 2} cy={shellY + shellH - 2} rx={3 + stage} ry={2} fill="#6EE7B7" />}

      {/* Head */}
      <circle cx={headX} cy={headY} r={headR} fill="#6EE7B7" />
      {/* Head spots */}
      {stage >= 3 && <>
        <circle cx={headX + 3} cy={headY - headR + 3} r={1.5} fill="#34D399" opacity="0.5" />
        <circle cx={headX - 2} cy={headY - headR + 5} r={1} fill="#34D399" opacity="0.4" />
      </>}

      {/* Eye */}
      {renderEyes()}

      {/* Cheek */}
      <circle cx={headX - 4} cy={headY + 3} r={2.5} fill="#FCA5A5" opacity="0.25" />

      {/* Mouth */}
      {mood === 'eating' ? (
        <ellipse cx={headX - 3} cy={headY + 5} rx="2.5" ry="2" fill="#047857" />
      ) : mood === 'happy' || mood === 'excited' ? (
        <path d={`M${headX - 5} ${headY + 4} Q${headX - 2} ${headY + 7} ${headX + 1} ${headY + 4}`} stroke="#047857" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      ) : mood === 'sad' || mood === 'hungry' ? (
        <path d={`M${headX - 4} ${headY + 6} Q${headX - 2} ${headY + 3} ${headX} ${headY + 6}`} stroke="#047857" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      ) : (
        <path d={`M${headX - 3} ${headY + 4} L${headX} ${headY + 4}`} stroke="#047857" strokeWidth="1.5" strokeLinecap="round" />
      )}

      {/* Bubbles */}
      {stage >= 3 && <>
        <circle cx={headX - headR - 5} cy={headY - 10} r="2.5" fill="#38BDF8" opacity="0.15" />
        <circle cx={headX - headR - 8} cy={headY - 16} r="1.8" fill="#38BDF8" opacity="0.12" />
        <circle cx={headX - headR - 3} cy={headY - 20} r="1.2" fill="#38BDF8" opacity="0.08" />
      </>}

      {/* Adult crown/gem */}
      {stage === 4 && (
        <g>
          <path d={`M${headX - 7} ${headY - headR} L${headX - 4} ${headY - headR - 10} L${headX} ${headY - headR - 4} L${headX + 4} ${headY - headR - 10} L${headX + 7} ${headY - headR}`}
            fill="#FBBF24" stroke="#F59E0B" strokeWidth="1" />
          <circle cx={headX} cy={headY - headR - 6} r="1.5" fill="#38BDF8" />
        </g>
      )}

      {/* Eating food */}
      {mood === 'eating' && <circle cx={headX - headR - 2} cy={headY + 2} r="2.5" fill="#34D399" />}
    </svg>
  );
}

// === ACCESSORY OVERLAYS (SVG-native) ===
function AccessoryOverlay({ accessories = [], size = 120 }) {
  if (!accessories.length) return null;

  const hat = accessories.find(a => a.slot === 'hat');
  const neck = accessories.find(a => a.slot === 'neck');
  const bg = accessories.find(a => a.slot === 'background');

  return (
    <>
      {/* Background aura — rendered as animated circles */}
      {bg && (
        <div style={{ position: 'absolute', inset: -6, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {[...Array(6)].map((_, i) => (
            <motion.span
              key={i}
              animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.9, 1.1, 0.9] }}
              transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
              style={{
                position: 'absolute',
                fontSize: size * 0.12,
                transform: `rotate(${i * 60}deg) translateY(${-size * 0.46}px)`,
              }}
            >
              {bg.icon}
            </motion.span>
          ))}
        </div>
      )}
      {/* Hat — positioned at top */}
      {hat && (
        <motion.div
          animate={{ rotate: [-2, 2, -2] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', top: size * 0.01, left: '50%', transform: 'translateX(-50%)',
            fontSize: size * 0.2, lineHeight: 1, pointerEvents: 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
          }}
        >
          {hat.icon}
        </motion.div>
      )}
      {/* Neck — positioned at center-bottom */}
      {neck && (
        <div style={{
          position: 'absolute', bottom: size * 0.2, left: '50%', transform: 'translateX(-50%)',
          fontSize: size * 0.15, lineHeight: 1, pointerEvents: 'none', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
        }}>
          {neck.icon}
        </div>
      )}
    </>
  );
}

// === AI-GENERATED PET IMAGES ===
const PET_IMAGES = {
  harimau: {
    1: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/c0f4663f8535b20dc5dba4df9d4fed59302326073d3055ba8664f9bb26f6bd37.png',
    2: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/ead44fe4a1ade28f37de66f2207b18b5709858bc8ef396dabaffada2822c7e7b.png',
    3: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/d6f43f400634f743f9407802fd87c231fd7b98eb74cbc5b3158d2d27d530a6ce.png',
    4: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/e7a0b4fbc93a3b8f3fb0d8b679f61ae57e3903b319a2e2942b65388521d6ea8e.png',
  },
  enggang: {
    1: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/2e53d6edd6aeb84da23894dbf8d6602f2e6e0896056c4056dd111f6e6a4889d1.png',
    2: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/56de7573712d62c1defdbc389f2c9a3d81f7e52f60278d382a5eefdec04a223a.png',
    3: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/99fb3c35c0e5d32bb5691fe9b4b3dd96719ee3f67f735f591c4fe2479928f6da.png',
    4: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/bc499de387744e318ae8c20116f852a3bee6342b563050bc8738b270bef90410.png',
  },
  penyu: {
    1: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/66a48123e35f64d10deb99c471fa79254955a8310b5d27ebd0c396e2c6ffb05a.png',
    2: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/c0152d1b9b2526c11bb9b4b205a8e7744088617cd94d821806ffdb19034706e3.png',
    3: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/87adf430a863728493e5e8fce1f36413ec162756c86eebb654dab0c3b395b85b.png',
    4: 'https://static.prod-images.emergentagent.com/jobs/ba4ec0b8-f4e4-4684-b7ad-5091c549dac2/images/efe9d0bfdcf379989019152f906f6cfd525024bf371696b5c6ca34c24100ee9d.png',
  },
};

function PetImage({ petType, stage, size, mood }) {
  const imageUrl = PET_IMAGES[petType]?.[stage] || PET_IMAGES[petType]?.[1];
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);

  const SvgFallback = { harimau: HarimauSprite, enggang: EnggangSprite, penyu: PenyuSprite }[petType] || HarimauSprite;

  if (!imageUrl || error) {
    return <SvgFallback stage={stage} size={size} mood={mood} />;
  }

  // Mood visual effects overlayed on the image
  const moodOverlay = () => {
    if (mood === 'sleeping') {
      return (
        <div style={{ position: 'absolute', top: '5%', right: '10%', fontSize: size * 0.14, fontWeight: 'bold', color: '#94a3b8', opacity: 0.7 }}>
          z z z
        </div>
      );
    }
    if (mood === 'eating') {
      return (
        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
          transition={{ duration: 0.6, repeat: Infinity }}
          style={{ position: 'absolute', bottom: '25%', right: '8%', fontSize: size * 0.18 }}
        >
          🍎
        </motion.div>
      );
    }
    if (mood === 'excited') {
      return (<>
        <motion.div animate={{ opacity: [0, 1, 0], y: [-5, -15] }} transition={{ duration: 1, repeat: Infinity }}
          style={{ position: 'absolute', top: '0%', left: '15%', fontSize: size * 0.1 }}>⭐</motion.div>
        <motion.div animate={{ opacity: [0, 1, 0], y: [-5, -15] }} transition={{ duration: 1, delay: 0.3, repeat: Infinity }}
          style={{ position: 'absolute', top: '0%', right: '15%', fontSize: size * 0.1 }}>⭐</motion.div>
      </>);
    }
    if (mood === 'sad' || mood === 'hungry') {
      return (
        <motion.div animate={{ opacity: [0, 0.7, 0], y: [0, 8] }} transition={{ duration: 2, repeat: Infinity }}
          style={{ position: 'absolute', top: '30%', left: '20%', fontSize: size * 0.08 }}>
          💧
        </motion.div>
      );
    }
    return null;
  };

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      {!loaded && <SvgFallback stage={stage} size={size} mood={mood} />}
      <img
        src={imageUrl}
        alt={petType}
        width={size}
        height={size}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        style={{
          position: loaded ? 'relative' : 'absolute',
          top: 0, left: 0,
          width: size, height: size,
          objectFit: 'contain',
          display: loaded ? 'block' : 'none',
          filter: mood === 'sleeping' ? 'brightness(0.7)' : mood === 'sad' ? 'saturate(0.6)' : 'none',
          transition: 'filter 0.5s',
        }}
      />
      {loaded && moodOverlay()}
    </div>
  );
}

// === MAIN EXPORT ===
export function PetSprite({ petType, stage = 1, size = 120, mood = 'happy', animate = true, accessories = [] }) {
  const hasAiImage = PET_IMAGES[petType]?.[stage] || PET_IMAGES[petType]?.[1];

  const sprite = (
    <div style={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
      <AccessoryOverlay accessories={accessories} size={size} />
      {hasAiImage
        ? <PetImage petType={petType} stage={stage} size={size} mood={mood} />
        : React.createElement({ harimau: HarimauSprite, enggang: EnggangSprite, penyu: PenyuSprite }[petType] || HarimauSprite, { stage, size, mood })
      }
    </div>
  );

  if (!animate) return sprite;

  const idleAnimation = mood === 'sleeping'
    ? { y: [0, -1, 0], rotate: [0, 1, 0, -1, 0] }
    : mood === 'excited'
    ? { y: [0, -8, 0], scale: [1, 1.05, 1] }
    : mood === 'eating'
    ? { y: [0, -2, 0], rotate: [0, 2, -2, 0] }
    : { y: [0, -4, 0] };

  const idleDuration = mood === 'sleeping' ? 4 : mood === 'excited' ? 1.5 : 2.5;

  return (
    <motion.div
      animate={idleAnimation}
      transition={{ repeat: Infinity, duration: idleDuration, ease: 'easeInOut' }}
      style={{ display: 'inline-flex' }}
    >
      {sprite}
    </motion.div>
  );
}

export function PetMiniSprite({ petType, stage = 1, size = 40 }) {
  return <PetSprite petType={petType} stage={stage} size={size} mood="happy" animate={false} />;
}
